import React, { useCallback, useState } from 'react';
import { Dialog, DialogBody, DialogHeader, Icon, showToast } from '@repo/ui';
import { get, size } from 'lodash';
import {
  useMarkWriteOff,
  useUploadMatterInvoiceSummaryFiles,
  useMatterInvoiceSummaryFilesQuery,
} from '../../queries/mattersQuery';
import { Appeal, FileCategory, MatterInvoiceSummaryFile } from '../../types';
import { NoAppealForm } from '../common/no-appeal';

interface ResolutionModalProps {
  isOpen: boolean;
  invoiceId: string;
  invoiceNumber: string;
  onClose: () => void;
  basicInformation: Partial<Appeal>;
}

/**
 * Modal wrapper around the NoAppealForm component.
 * Collects supporting files for a write-off workflow.
 */
export const MarkNoAppealModal: React.FC<ResolutionModalProps> = ({
  isOpen,
  invoiceId,
  invoiceNumber,
  basicInformation,
  onClose,
}) => {
  const { mutateAsync: markWriteOff } = useMarkWriteOff(invoiceId);
  const { mutateAsync: uploadFiles } = useUploadMatterInvoiceSummaryFiles(
    invoiceId,
    'AR Write-Off',
    {
      isShowToast: false,
      category: FileCategory.PAYMENT_SUPPORT,
    }
  );
  const [errors, setErrors] = useState<{
    comment: string;
    files: string;
  }>({
    comment: '',
    files: '',
  });

  const [form, setForm] = useState<{
    comment: string;
    files: File[];
  }>({
    comment: '',
    files: [],
  });

  const [isLoading, setIsLoading] = useState(false);
  const [selectedExistingFiles, setSelectedExistingFiles] = useState<MatterInvoiceSummaryFile[]>(
    []
  );

  const { data: existingFilesResponse, isLoading: isLoadingExistingFiles } =
    useMatterInvoiceSummaryFilesQuery(invoiceId, { enabled: isOpen });

  const existingFiles = existingFilesResponse?.data || [];

  const handleFilesChange = useCallback(
    (nextFiles: File[]) => {
      setForm(prev => ({ ...prev, files: nextFiles }));
      // Clear error if either new files or existing files are selected
      const hasFiles = nextFiles.length > 0 || selectedExistingFiles.length > 0;
      setErrors(prev => ({
        ...prev,
        files: hasFiles ? '' : prev.files,
      }));
    },
    [selectedExistingFiles.length]
  );

  const handleExistingFilesChange = useCallback(
    (files: MatterInvoiceSummaryFile[]) => {
      setSelectedExistingFiles(files);
      // Clear error if either new files or existing files are selected
      const hasFiles = form.files.length > 0 || files.length > 0;
      setErrors(prev => ({
        ...prev,
        files: hasFiles ? '' : prev.files,
      }));
    },
    [form.files.length]
  );

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    setForm({
      comment: '',
      files: [],
    });
    setErrors({
      comment: '',
      files: '',
    });
    setSelectedExistingFiles([]);
    onClose();
  };

  const handleInputChange = useCallback((field: 'comment', value: string) => {
    if (field === 'comment') {
      setForm(prev => ({ ...prev, comment: value }));
      setErrors(prev => ({ ...prev, comment: '' }));
    }
  }, []);

  const handleSetError = useCallback((field: 'comment' | 'files', error: string) => {
    setErrors(prev => ({ ...prev, [field]: error }));
  }, []);

  /**
   * Handles the submission of a write-off request, including file uploads.
   * * This client-side function implements a secure two-step file upload process for each file:
   * 1. Calls the backend API to generate a temporary, signed upload URL (Presigned URL) for S3.
   * 2. Uploads the file directly to S3 using the received Presigned URL.
   * * After all files are successfully uploaded, it submits the final write-off request (including the
   * S3 keys of the newly uploaded files) to the backend via `markWriteOff`.
   *
   * @async
   * @function handleSubmit
   * @returns {Promise<void>} A promise that resolves when the write-off is successfully marked
   * and the submission process is complete.
   * @throws {Error} Throws an error if any step (Presigned URL generation, file upload, or
   * final write-off submission) fails, which triggers an error notification to the user.
   */
  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      const payload = {
        comment: form.comment,
      };

      let filesPayload = [] as MatterInvoiceSummaryFile[];

      // Include selected existing files
      if (size(selectedExistingFiles) > 0) {
        filesPayload = [...filesPayload, ...selectedExistingFiles];
      }

      // Upload new files and add them to payload
      if (size(form.files) > 0) {
        const uploadResult = await uploadFiles({
          files: form.files,
          successCallback: async response => {
            filesPayload = [
              ...filesPayload,
              ...(get(response, 'successFiles', []) as MatterInvoiceSummaryFile[]),
            ];
          },
        });

        // Check if there are any error files and stop the process
        if (uploadResult.errorFiles && uploadResult.errorFiles.length > 0) {
          return;
        }
      }
      // Validate at least one file exists
      if (size(filesPayload) === 0) {
        setErrors(prev => ({
          ...prev,
          files: 'At least one attachment is required',
        }));
        return;
      }

      await markWriteOff({ ...payload });
      handleClose();
    } catch (error) {
      showToast.error(
        `Mark write-off failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      size="xxllg"
      className="!max-h-[90vh] overflow-y-auto"
      showCloseButton={false}
    >
      <DialogHeader className="sticky top-0 bg-white dark:bg-xtnd-dark-800 z-[51]">
        <div className="flex justify-between">
          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
            Mark Write-Off - INV #{invoiceNumber}
          </h3>
          <button
            type="button"
            className="absolute z-[52] top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 transition-colors dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-xtnd-dark-700"
            onClick={handleClose}
            aria-label="Close dialog"
          >
            <Icon name="HiOutlineX" />
          </button>
        </div>
      </DialogHeader>

      <DialogBody className="space-y-4">
        <NoAppealForm
          invoiceId={invoiceId}
          isOpen={isOpen}
          matterDisplayNumber={basicInformation?.matterDisplayNumber ?? ''}
          clientName={basicInformation?.clientName ?? ''}
          invoiceDate={basicInformation?.invoiceDate ?? ''}
          invoiceNumber={basicInformation?.invoiceNumber ?? invoiceNumber}
          outstandingAmount={basicInformation?.amountDue ?? 0}
          amountPaid={basicInformation?.paid ?? 0}
          totalAmount={basicInformation?.totalAmount ?? 0}
          creditsIssued={basicInformation?.creditsIssued ?? 0}
          responsibleAttorneyName={basicInformation?.responsibleAttorneyName ?? ''}
          isLoadingExistingFiles={isLoadingExistingFiles}
          comment={form.comment}
          files={form.files}
          errors={errors}
          isLoading={isLoading}
          onInputChange={handleInputChange}
          onFilesChange={handleFilesChange}
          onExistingFilesChange={handleExistingFilesChange}
          onSubmit={handleSubmit}
          setError={handleSetError}
          existingFiles={existingFiles}
          selectedExistingFiles={selectedExistingFiles}
        />
      </DialogBody>
    </Dialog>
  );
};

export default MarkNoAppealModal;
